import { useTranslation } from "react-i18next";
import { cn } from "@core/utils";
import { Button } from "./Button";

interface ErrorPanelProps {
  /** Asks again. */
  onRetry: () => void;
  isRetrying?: boolean;
  /** Overrides the generic title (« Cette page n'a pas pu s'afficher »). */
  title?: string;
  /** Overrides the generic line under the title. */
  description?: string;
  className?: string;
}

/**
 * What a part of a screen shows when its data failed to load: a bordered card, what happened, and
 * a retry. The generic words are shared (`common`), so every list says the same thing; a screen
 * may pass its own title or line. A route that fails as a whole shows the same panel through
 * `ContentError`.
 */
export function ErrorPanel({
  onRetry,
  isRetrying = false,
  title,
  description,
  className,
}: ErrorPanelProps) {
  const { t } = useTranslation("common");

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-start gap-md rounded-lg border border-(--border-default) bg-surface-card p-xl",
        className
      )}
    >
      <div className="flex flex-col gap-xs">
        <h2 className="text-lg font-semibold text-(--text-primary)">
          {title ?? t("layout.content-error-title")}
        </h2>
        <p className="text-sm-regular text-(--text-secondary)">
          {description ?? t("layout.content-error-description")}
        </p>
      </div>
      <Button
        variant="secondary"
        onPress={onRetry}
        isPending={isRetrying}
        isDisabled={isRetrying}
      >
        {t("actions.retry")}
      </Button>
    </div>
  );
}

export type { ErrorPanelProps };
